import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { queryOptions } from "react-query";

export type HeaderNavigationLinkAudience = "logged_in" | "logged_out";
export type HeaderNavigationLinkItemKind =
  | "custom"
  | "home"
  | "members"
  | "events"
  | "courses"
  | "leaderboard"
  | "newsletter_homepage";
export type HeaderNavigationLinkVisibility =
  | "enabled"
  | "disabled"
  | "admin_only";
export type HeaderNavigationLinkItemAudience =
  | "logged_in"
  | "logged_out"
  | "everyone";

export interface HeaderNavigationLinkItem {
  audience: HeaderNavigationLinkItemAudience;
  community_id: number;
  id: number;
  kind: HeaderNavigationLinkItemKind;
  name: string;
  open_in_new_window: boolean;
  url: string | null;
  visibility: HeaderNavigationLinkVisibility;
}

const HEADER_NAV_ITEMS_CACHE_TIME = 24 * 60 * 60 * 1000; // 24 hours
const HEADER_NAV_ITEMS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

export const headerNavigationLinkKey = createQueryKeyFactory(
  "header-navigation-link",
);

const listKey = (audience?: HeaderNavigationLinkAudience) =>
  audience
    ? headerNavigationLinkKey.key("list", audience)
    : headerNavigationLinkKey.key("list");
const detailKey = (id: number) => headerNavigationLinkKey.key("detail", id);

export const headerNavigationLinkQueries = {
  list: (audience?: HeaderNavigationLinkAudience) =>
    queryOptions({
      queryKey: listKey(audience),
      queryFn: (): Promise<HeaderNavigationLinkItem[]> =>
        reactQueryGet<HeaderNavigationLinkItem[]>(
          internalApi.settings.navigation.list(
            audience ? { params: { audience } } : {},
          ),
        ),
      staleTime: HEADER_NAV_ITEMS_STALE_TIME,
      gcTime: HEADER_NAV_ITEMS_CACHE_TIME,
    }),
  detail: (id: number) =>
    queryOptions({
      queryKey: detailKey(id),
      queryFn: (): Promise<HeaderNavigationLinkItem> =>
        reactQueryGet<HeaderNavigationLinkItem>(
          internalApi.settings.navigation.show({ id }),
        ),
    }),
};

export const headerNavigationLinkInvalidations = {
  all: () =>
    queryClient.invalidateQueries({ queryKey: headerNavigationLinkKey.root() }),
  list: (audience?: HeaderNavigationLinkAudience) =>
    queryClient.invalidateQueries({ queryKey: listKey(audience) }),
  detail: (id: number) =>
    queryClient.invalidateQueries({
      queryKey: detailKey(id),
    }),
  removeDetail: (id: number) =>
    queryClient.removeQueries({
      queryKey: detailKey(id),
    }),
};
